import React, { useMemo, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { AllocationType, AssetAllocation, Strategy, StrategyType } from '@ndvr/portfolio-api';

import { useAssetsByIdsMap } from '@/hooks/market-data-queries';
import { cn } from '@/utils/cn';

import { calculateTotalWeight } from '../../shared-utils';
import {
    getHoldingsCountLabel,
    getStrategyHoldingsLabel,
    getStrategyTypeBadge,
    getStrategyTypeLabel,
} from '../../strategies/strategy-utils';
import { getStrategyDescription } from '../../strategies/strategyDescriptions';
import { AddStrategyRow } from './AddStrategyRow';
import { ModelFormValues, StrategyElementFormValue } from './model-form-schema';
import { ModelExposurePanel } from './ModelExposurePanel';
import {
    holdingsFromDraft,
    holdingsFromStrategy,
    ModelStrategyHolding,
    ModelStrategyHoldings,
} from './ModelStrategyHoldings';
import { ModelStrategyRow } from './ModelStrategyRow';
import { NewStrategyBuilderPanel } from './NewStrategyBuilderPanel';
import { useModelStrategyElements } from './useModelStrategyElements';

const TYPE_OPTIONS: Array<{ value: AllocationType.PERSONAL | AllocationType.SHARED; label: string }> = [
    { value: AllocationType.PERSONAL, label: 'Personal' },
    { value: AllocationType.SHARED, label: 'Shared' },
];

interface ResolvedStrategyElement {
    element: StrategyElementFormValue;
    /** Set for 'existing' elements whose GUID is still in the library; undefined for drafts and stale GUIDs. */
    strategy?: Strategy;
    holdings: ModelStrategyHolding[];
}

// Saved strategies carry decimal weights, strategies drafted in this form keep percent weights,
// so each kind goes through its own holdings mapper.
const resolveStrategyElement = (
    element: StrategyElementFormValue,
    availableStrategies: Strategy[],
): ResolvedStrategyElement => {
    if (element.mode === 'existing') {
        const strategy = availableStrategies.find((s) => s.strategyGUID === element.existingStrategyGUID);
        return { element, strategy, holdings: holdingsFromStrategy(strategy?.assetAllocations ?? []) };
    }
    return { element, holdings: holdingsFromDraft(element.newStrategyAssetAllocations ?? []) };
};

interface ModelFormBodyProps {
    form: UseFormReturn<ModelFormValues>;
    showTypeSelector: boolean;
    /** Whether the Personal/Shared toggle is interactive — e.g. type can't change once a model already exists. */
    canChangeType?: boolean;
    availableStrategies: Strategy[];
    existingStrategyNames: string[];
    onCommitNewStrategy: (name: string, assetAllocations: AssetAllocation[]) => void;
    nameInputRef?: { current: HTMLInputElement | null };
    autoFocusName?: boolean;
}

export const ModelFormBody: React.FC<ModelFormBodyProps> = ({
    form,
    showTypeSelector,
    canChangeType = true,
    availableStrategies,
    existingStrategyNames,
    onCommitNewStrategy,
    nameInputRef,
    autoFocusName,
}) => {
    const [query, setQuery] = useState('');
    const [showBuilder, setShowBuilder] = useState(false);
    const allocationType = form.watch('allocationType');
    const modelName = form.watch('allocationName');
    const errors = form.formState.errors;
    const { ref: registerNameRef, ...nameFieldProps } = form.register('allocationName');

    const { elements, addExisting, removeElement, updateWeight, evenSplit } = useModelStrategyElements(form);

    const usedStrategyGUIDs = new Set(
        elements
            .filter((el) => el.mode === 'existing' && el.existingStrategyGUID)
            .map((el) => el.existingStrategyGUID!),
    );
    const usedNewNames = elements
        .filter((el) => el.mode === 'new' && el.newStrategyName)
        .map((el) => el.newStrategyName!);

    const resolvedElements = useMemo(
        () => elements.map((element) => resolveStrategyElement(element, availableStrategies)),
        [elements, availableStrategies],
    );
    const securityIds = useMemo(
        () =>
            Array.from(
                new Set(
                    resolvedElements.flatMap(({ holdings }) =>
                        holdings.flatMap((holding) => [holding.securityId, ...holding.replacementSecurityIds]),
                    ),
                ),
            ),
        [resolvedElements],
    );
    const { data: assetsBySecurityId } = useAssetsByIdsMap(securityIds, { throwOnError: false });
    const getAssetInfo = (securityId: string) => assetsBySecurityId.get(securityId);

    const expandHoldingsByDefault = elements.length === 1;

    const renderStrategyDetails = (strategy: Strategy | undefined, holdings: ModelStrategyHolding[]) => {
        const description =
            strategy?.strategyType === StrategyType.CORE
                ? getStrategyDescription(strategy.growthAllocationType)
                : undefined;
        if (description) {
            return <div className="text-cocoa-700">{description}</div>;
        }
        return <ModelStrategyHoldings holdings={holdings} getAssetInfo={getAssetInfo} />;
    };

    const totalWeight = calculateTotalWeight(elements);
    const displayWeight = Math.round(totalWeight * 100) / 100;
    const isValidTotal = displayWeight === 100;

    return (
        <div className="flex flex-wrap items-start gap-6">
            <div className="flex min-w-0 flex-1 basis-[480px] flex-col gap-5">
                <div className="border-cocoa-200 flex flex-col gap-3.5 rounded-lg border bg-white p-6">
                    <label htmlFor="model-name" className="text-cocoa-500 text-xs">
                        Model name
                    </label>
                    <div className="flex flex-wrap items-center gap-3">
                        <input
                            id="model-name"
                            {...nameFieldProps}
                            ref={(el) => {
                                registerNameRef(el);
                                if (nameInputRef) nameInputRef.current = el;
                            }}
                            placeholder="Name this model"
                            autoFocus={autoFocusName}
                            className="font-heading border-cocoa-200 text-cocoa-1000 placeholder:text-cocoa-300 min-w-[220px] flex-1 border-0 border-b bg-transparent pb-0.5 text-[28px] leading-[1.4] font-normal tracking-[-0.02em] outline-none"
                        />
                        {showTypeSelector && (
                            <div className="flex gap-1.5">
                                {TYPE_OPTIONS.map((option) => (
                                    <button
                                        key={option.value}
                                        type="button"
                                        disabled={!canChangeType}
                                        onClick={() => form.setValue('allocationType', option.value)}
                                        className={cn(
                                            'rounded px-2 py-1.5 text-xs font-semibold tracking-wide uppercase',
                                            allocationType === option.value
                                                ? 'bg-cocoa-900 text-white'
                                                : 'bg-cocoa-0 text-cocoa-500',
                                            canChangeType ? 'hover:bg-cocoa-100' : 'cursor-not-allowed opacity-50',
                                        )}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                    {errors.allocationName && <p className="text-sm text-red-600">{errors.allocationName.message}</p>}
                </div>

                <AddStrategyRow
                    query={query}
                    onQueryChange={setQuery}
                    availableStrategies={availableStrategies}
                    usedStrategyGUIDs={usedStrategyGUIDs}
                    allocationType={allocationType}
                    onAddExisting={addExisting}
                    onOpenBuilder={() => setShowBuilder(true)}
                />

                {showBuilder && (
                    <NewStrategyBuilderPanel
                        modelName={modelName}
                        existingStrategyNames={[...existingStrategyNames, ...usedNewNames]}
                        onCommit={(name, assetAllocations) => {
                            onCommitNewStrategy(name, assetAllocations);
                            setShowBuilder(false);
                        }}
                        onCancel={() => setShowBuilder(false)}
                    />
                )}

                <div className="flex flex-col gap-3">
                    {resolvedElements.length === 0 ? (
                        <div className="border-cocoa-300 rounded-lg border border-dashed bg-white px-6 py-10 text-center">
                            <span className="text-cocoa-500 text-sm">
                                A Model needs at least one strategy. Add one above.
                            </span>
                        </div>
                    ) : (
                        resolvedElements.map(({ element, strategy, holdings }, index) => {
                            const rowProps = {
                                details: renderStrategyDetails(strategy, holdings),
                                defaultExpanded: expandHoldingsByDefault,
                                weight: element.weight,
                                weightError: errors.strategyElements?.[index]?.weight?.message,
                                onUpdateWeight: (weight: string) => updateWeight(index, weight),
                                onRemove: () => removeElement(index),
                            };
                            if (element.mode === 'existing') {
                                const badge = strategy ? getStrategyTypeBadge(strategy) : undefined;
                                return (
                                    <ModelStrategyRow
                                        key={`existing-${element.existingStrategyGUID}`}
                                        badgeLabel={
                                            badge?.label ?? (strategy ? getStrategyTypeLabel(strategy) : 'Strategy')
                                        }
                                        badgeTone={badge?.tone ?? 'neutral'}
                                        name={strategy?.strategyName || 'Unknown strategy'}
                                        description={strategy ? getStrategyHoldingsLabel(strategy) : '0 holdings'}
                                        {...rowProps}
                                    />
                                );
                            }
                            return (
                                <ModelStrategyRow
                                    key={`new-${element.newStrategyName ?? index}`}
                                    badgeLabel="New"
                                    name={element.newStrategyName || 'Untitled strategy'}
                                    description={getHoldingsCountLabel(holdings.length)}
                                    {...rowProps}
                                />
                            );
                        })
                    )}

                    {typeof errors.strategyElements?.message === 'string' && (
                        <p className="text-sm text-red-600">{errors.strategyElements.message}</p>
                    )}

                    {elements.length > 0 && (
                        <div className="flex items-center gap-3">
                            <span
                                className={`text-sm font-medium ${isValidTotal ? 'text-cocoa-1000' : 'text-red-600'}`}
                            >
                                Total weight: {displayWeight}%
                            </span>
                            {!isValidTotal && <span className="text-cocoa-500 text-xs">Weights must sum to 100%.</span>}
                            <button
                                type="button"
                                onClick={evenSplit}
                                className="border-cocoa-200 hover:bg-cocoa-0 ml-auto rounded-md border bg-white px-3 py-1.5 text-sm font-medium"
                            >
                                Even split
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <div className="w-full min-w-0 flex-1 basis-[260px] sm:max-w-[340px]">
                <ModelExposurePanel elements={elements} />
            </div>
        </div>
    );
};
